import {TextInput, Textarea} from '@mantine/core';
import {useEffect, useRef, useState} from 'react';
import {NotesText} from './NotesText';

type Props = {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
  displayClassName?: string;
  renderAsLinks?: boolean;
  className?: string;
};

export const ClickToEditInput: React.FC<Props> = ({
  value,
  onSave,
  placeholder = '未設定',
  multiline = false,
  disabled = false,
  displayClassName = '',
  renderAsLinks = false,
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const previousValueRef = useRef(value);

  useEffect(() => {
    setLocalValue(value);
    previousValueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== previousValueRef.current) {
      onSave(localValue);
      previousValueRef.current = localValue;
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      setLocalValue(previousValueRef.current);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    const commonProps = {
      value: localValue,
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => setLocalValue(e.target.value),
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder,
      size: 'xs' as const,
      classNames: {
        input: 'text-xs pc:text-sm',
      },
    };

    const InputComponent = multiline ? (
      <Textarea
        {...commonProps}
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        autosize
        minRows={2}
        maxRows={10}
      />
    ) : (
      <TextInput
        {...commonProps}
        ref={inputRef as React.RefObject<HTMLInputElement>}
      />
    );

    return <div className={`w-full ${className}`}>{InputComponent}</div>;
  }

  const buttonBaseClasses =
    'w-full cursor-pointer hover:bg-background-2 rounded px-8 py-4 min-h-[32px] text-left text-xs pc:text-sm';
  const layoutClasses = renderAsLinks ? '' : 'flex items-center';
  const disabledClasses = disabled ? 'cursor-not-allowed opacity-50' : '';
  const buttonClasses = `${buttonBaseClasses} ${layoutClasses} ${displayClassName} ${className} ${disabledClasses}`;

  const displayContent = value ? (
    renderAsLinks ? (
      <NotesText>{value}</NotesText>
    ) : (
      <span className="break-all">{value}</span>
    )
  ) : (
    <span className="text-black-3">{placeholder}</span>
  );

  return (
    <button
      onClick={handleClick}
      className={buttonClasses}
      disabled={disabled}
      type="button"
    >
      {displayContent}
    </button>
  );
};
